import React, { useState, useEffect } from 'react';
import styles from './index.module.scss';

const MAX_SLIDE_CAPTIONS = 10;
const SLIDE_TRANSITION_MS = 850;
const AUTO_VISIBLE_MS = 3600;
const AUTO_TRANSITION_MS = 850;
const CAPTION_BASE_DELAY_S = 0.12;
const CAPTION_LINE_STEP_S = 0.16;
const DETAILS_EXTRA_DELAY_S = 0.45;

const getDetailsButtonDelay = (captionSegments) =>
  CAPTION_BASE_DELAY_S +
  captionSegments.filter(Boolean).length * CAPTION_LINE_STEP_S +
  DETAILS_EXTRA_DELAY_S;

const normalizeContentText = (rawValue) => {
  const text = String(rawValue ?? '').trim().replace(/^"+|"+$/g, '');
  if (!text || text.toLowerCase() === 'none') return '';
  return text;
};

const splitCaptionToSegments = (caption) => {
  const lines = String(caption || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 3);

  return [lines[0] || '', lines[1] || '', lines[2] || ''];
};

const SWIPE_THRESHOLD_PX = 40;
const DRAG_COMMIT_RATIO = 0.2;
const EDGE_DRAG_RESISTANCE = 0.35;

const MainSlider = ({ publication, globalIndex }) => {
  void globalIndex;
  const [publicationData, setPublicationData] = useState(null);
  const [contentData, setContentData] = useState(null);
  const [path_to_folder, setPath_to_folder] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [captionSlide, setCaptionSlide] = useState(0);
  const [captionAnimate, setCaptionAnimate] = useState(false);
  const [isAutoTransitioning, setIsAutoTransitioning] = useState(false);
  const [slideTransitionMode, setSlideTransitionMode] = useState('auto');
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const sliderContainerRef = React.useRef(null);
  const dragStartRef = React.useRef({ x: 0, y: 0, active: false });
  const dragAxisRef = React.useRef(null);
  const isMouseDraggingRef = React.useRef(false);
  const isDragModeRef = React.useRef(false);
  const currentSlideRef = React.useRef(0);
  const slidesLengthRef = React.useRef(0);
  const autoSlideTimerRef = React.useRef(null);

  useEffect(() => {
    if (publication) setPublicationData(publication);
  }, [publication]);

  useEffect(() => {
    if (!publicationData) return;
    if (publicationData.content) {
      try {
        const parsed = JSON.parse(publicationData.content);
        setContentData(Array.isArray(parsed) ? parsed : []);
      } catch {
        setContentData([]);
      }
    } else {
      setContentData([]);
    }
    if (publicationData.path && publicationData.eng_name) {
      setPath_to_folder(`https://${publicationData.path}${publicationData.eng_name}/`);
    }
  }, [publicationData]);

  const slides = React.useMemo(() => {
    if (!contentData || !Array.isArray(contentData) || !path_to_folder) return [];

    for (const block of contentData) {
      if (!block?.block_content || !Array.isArray(block.block_content)) continue;

      const blockContent = block.block_content;
      const imgsBlock = blockContent[0];
      const isImgsBlock =
        imgsBlock &&
        (imgsBlock.cotent_type === 'imgs_block' || imgsBlock.content_type === 'imgs_block');

      if (!isImgsBlock || !imgsBlock?.imgs_names) continue;

      let namesArr = [];
      try {
        namesArr =
          typeof imgsBlock.imgs_names === 'string'
            ? JSON.parse(imgsBlock.imgs_names)
            : imgsBlock.imgs_names;
      } catch {
        continue;
      }

      if (!Array.isArray(namesArr)) continue;

      const imgNames = namesArr
        .filter((name) => name && name !== 'none')
        .map((name) =>
          name && name.includes('\\') ? name.substring(name.lastIndexOf('\\') + 1) : name,
        );

      const captionTexts = blockContent
        .slice(1, 1 + MAX_SLIDE_CAPTIONS)
        .map((item) => normalizeContentText(item?.content_text));

      let signaturesArr = [];
      if (imgsBlock.imgs_signatures && imgsBlock.imgs_signatures !== 'none' && imgsBlock.imgs_signatures !== '[]') {
        try {
          const parsed =
            typeof imgsBlock.imgs_signatures === 'string'
              ? JSON.parse(imgsBlock.imgs_signatures)
              : imgsBlock.imgs_signatures;
          signaturesArr = Array.isArray(parsed) ? parsed : [];
        } catch {
          signaturesArr = [];
        }
      }

      return imgNames.map((imgName, index) => {
        const rawSignature = signaturesArr[index];
        const signature =
          rawSignature != null &&
          String(rawSignature).trim() !== '' &&
          String(rawSignature).trim().toLowerCase() !== 'none'
            ? String(rawSignature).trim()
            : null;

        return {
          imgName,
          signature,
          caption: captionTexts[index] || '',
          captionSegments: splitCaptionToSegments(captionTexts[index] || ''),
        };
      });
    }

    return [];
  }, [contentData, path_to_folder]);

  React.useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);

  React.useEffect(() => {
    slidesLengthRef.current = slides.length;
  }, [slides.length]);

  const clearAutoSlideTimer = React.useCallback(() => {
    if (autoSlideTimerRef.current) {
      window.clearTimeout(autoSlideTimerRef.current);
      autoSlideTimerRef.current = null;
    }
  }, []);

  const scheduleAutoSlide = React.useCallback(() => {
    clearAutoSlideTimer();
    if (slides.length <= 1) return;

    const runAutoSlide = () => {
      const nextIndex = (currentSlideRef.current + 1) % slides.length;

      setSlideTransitionMode('auto');
      setIsAutoTransitioning(true);
      setCaptionAnimate(false);
      setCurrentSlide(nextIndex);

      autoSlideTimerRef.current = window.setTimeout(() => {
        setIsAutoTransitioning(false);
        setCaptionAnimate(true);
        setCaptionSlide(nextIndex);

        autoSlideTimerRef.current = window.setTimeout(runAutoSlide, AUTO_VISIBLE_MS);
      }, AUTO_TRANSITION_MS);
    };

    autoSlideTimerRef.current = window.setTimeout(runAutoSlide, AUTO_VISIBLE_MS);
  }, [clearAutoSlideTimer, slides.length]);

  useEffect(() => {
    scheduleAutoSlide();
    return clearAutoSlideTimer;
  }, [scheduleAutoSlide, clearAutoSlideTimer]);

  const goToSlide = React.useCallback(
    (index, { manual = false } = {}) => {
      if (slides.length === 0) return;

      const normalizedIndex = ((index % slides.length) + slides.length) % slides.length;

      if (manual) {
        setSlideTransitionMode('manual');
        setIsAutoTransitioning(false);
        setCaptionAnimate(false);
        setCurrentSlide(normalizedIndex);
        setCaptionSlide(normalizedIndex);
      } else {
        setSlideTransitionMode('auto');
        setIsAutoTransitioning(false);
        setCaptionAnimate(false);
        setCurrentSlide(normalizedIndex);
      }

      scheduleAutoSlide();
    },
    [slides.length, scheduleAutoSlide],
  );

  const prevSlide = () => {
    if (slides.length <= 1) return;
    goToSlide(currentSlide - 1, { manual: true });
  };

  const nextSlide = () => {
    if (slides.length <= 1) return;
    goToSlide(currentSlide + 1, { manual: true });
  };

  const handleDetailsClick = (e, href) => {
    e.preventDefault();
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
  };

  const getContainerWidth = () => sliderContainerRef.current?.offsetWidth ?? 0;

  const settleCaptionsAfterDrag = React.useCallback((targetSlideIndex) => {
    setCaptionAnimate(false);
    setCaptionSlide(targetSlideIndex);
  }, []);

  const applyDragOffset = React.useCallback((dx) => {
    const lastIndex = slidesLengthRef.current - 1;
    const slide = currentSlideRef.current;
    let nextDx = dx;

    if (slide === 0 && nextDx > 0) {
      nextDx *= EDGE_DRAG_RESISTANCE;
    } else if (slide === lastIndex && nextDx < 0) {
      nextDx *= EDGE_DRAG_RESISTANCE;
    }

    setDragOffsetPx(nextDx);
  }, []);

  const cancelDrag = React.useCallback(() => {
    dragStartRef.current.active = false;
    dragAxisRef.current = null;
    isMouseDraggingRef.current = false;
    isDragModeRef.current = false;
    setIsTouchDragging(false);
    setIsAutoTransitioning(false);
    setCaptionAnimate(false);
    setDragOffsetPx(0);
    scheduleAutoSlide();
  }, [scheduleAutoSlide]);

  const preparePointerInteraction = React.useCallback(
    (clientX, clientY) => {
      dragStartRef.current = { x: clientX, y: clientY, active: true };
      dragAxisRef.current = null;
      setIsAutoTransitioning(false);
      clearAutoSlideTimer();
    },
    [clearAutoSlideTimer],
  );

  const enterDragMode = React.useCallback(() => {
    if (isDragModeRef.current) return;
    isDragModeRef.current = true;
    setCaptionAnimate(false);
    setIsTouchDragging(true);
  }, []);

  const updateDrag = React.useCallback(
    (clientX, clientY, preventDefaultFn) => {
      if (!dragStartRef.current.active) return;

      const dx = clientX - dragStartRef.current.x;
      const dy = clientY - dragStartRef.current.y;

      if (dragAxisRef.current === null) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;

        if (Math.abs(dy) > Math.abs(dx) * 1.15) {
          dragAxisRef.current = 'vertical';
          cancelDrag();
          return;
        }

        dragAxisRef.current = 'horizontal';
      }

      if (dragAxisRef.current !== 'horizontal') return;

      preventDefaultFn?.();
      enterDragMode();
      applyDragOffset(dx);
    },
    [applyDragOffset, cancelDrag, enterDragMode],
  );

  const finishDrag = React.useCallback(
    (clientX) => {
      if (!dragStartRef.current.active) return;

      const startX = dragStartRef.current.x;
      const axis = dragAxisRef.current;
      const totalDx = clientX - startX;
      const width = getContainerWidth();
      const threshold = Math.max(SWIPE_THRESHOLD_PX, width * DRAG_COMMIT_RATIO);
      const slide = currentSlideRef.current;
      const lastIndex = slidesLengthRef.current - 1;

      dragStartRef.current.active = false;
      dragAxisRef.current = null;
      isMouseDraggingRef.current = false;

      if (!isDragModeRef.current || axis !== 'horizontal' || slidesLengthRef.current <= 1) {
        isDragModeRef.current = false;
        setIsTouchDragging(false);
        scheduleAutoSlide();
        return;
      }

      isDragModeRef.current = false;

      const goNext = totalDx <= -threshold && slide < lastIndex;
      const goPrev = totalDx >= threshold && slide > 0;

      if (goNext) {
        const nextIndex = slide + 1;
        setSlideTransitionMode('manual');
        setCurrentSlide(nextIndex);
        setDragOffsetPx(width + totalDx);

        window.requestAnimationFrame(() => {
          setIsTouchDragging(false);
          setDragOffsetPx(0);
        });

        window.setTimeout(() => {
          settleCaptionsAfterDrag(nextIndex);
        }, SLIDE_TRANSITION_MS);

        scheduleAutoSlide();
        return;
      }

      if (goPrev) {
        const prevIndex = slide - 1;
        setSlideTransitionMode('manual');
        setCurrentSlide(prevIndex);
        setDragOffsetPx(totalDx - width);

        window.requestAnimationFrame(() => {
          setIsTouchDragging(false);
          setDragOffsetPx(0);
        });

        window.setTimeout(() => {
          settleCaptionsAfterDrag(prevIndex);
        }, SLIDE_TRANSITION_MS);

        scheduleAutoSlide();
        return;
      }

      setIsTouchDragging(false);
      setDragOffsetPx(0);

      window.setTimeout(() => {
        settleCaptionsAfterDrag(slide);
      }, SLIDE_TRANSITION_MS);

      scheduleAutoSlide();
    },
    [scheduleAutoSlide, settleCaptionsAfterDrag],
  );

  const isInteractiveTarget = (target) =>
    target instanceof Element && target.closest('a, button, input, textarea, select, label');

  const handleTouchStart = (e) => {
    if (slidesLengthRef.current <= 1) return;
    if (isInteractiveTarget(e.target)) return;
    preparePointerInteraction(e.touches[0].clientX, e.touches[0].clientY);
  };

  const handleTouchMove = React.useCallback(
    (e) => {
      updateDrag(e.touches[0].clientX, e.touches[0].clientY, () => e.preventDefault());
    },
    [updateDrag],
  );

  React.useEffect(() => {
    const container = sliderContainerRef.current;
    if (!container) return undefined;

    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleTouchMove]);

  const handleTouchEnd = (e) => {
    finishDrag(e.changedTouches[0].clientX);
  };

  const handleTouchCancel = () => {
    cancelDrag();
  };

  const handleMouseDown = (e) => {
    if (slidesLengthRef.current <= 1) return;
    if (e.button !== 0) return;
    if (isInteractiveTarget(e.target)) return;

    isMouseDraggingRef.current = true;
    preparePointerInteraction(e.clientX, e.clientY);
  };

  React.useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isMouseDraggingRef.current) return;
      updateDrag(e.clientX, e.clientY);
    };

    const handleMouseUp = (e) => {
      if (!isMouseDraggingRef.current) return;
      finishDrag(e.clientX);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [finishDrag, updateDrag]);

  if (!path_to_folder || slides.length === 0) return null;

  return (
    <div className={styles.mainSlider}>
      <div
        ref={sliderContainerRef}
        className={`${styles.sliderContainer} ${isTouchDragging ? styles.sliderContainerDragging : ''}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        onMouseDown={handleMouseDown}>
        <div
          className={`${styles.slidesWrapper} ${isTouchDragging ? styles.slidesWrapperDragging : ''} ${
            slideTransitionMode === 'auto' && !isTouchDragging ? styles.slidesWrapperAuto : ''
          }`}
          style={{ transform: `translateX(calc(-${currentSlide * 100}% + ${dragOffsetPx}px))` }}>
          {slides.map((slide, index) => {
            const isCaptionVisible =
              !isAutoTransitioning && (!captionAnimate || index === captionSlide);
            const isCaptionInstant = !captionAnimate || index !== captionSlide;

            return (
            <div key={index} className={styles.slide}>
              <div className={styles.slideContent}>
                <img
                  src={path_to_folder + slide.imgName}
                  alt={`Слайд ${index + 1}`}
                  className={styles.slideImage}
                  draggable={false}
                />
                {(slide.captionSegments?.some(Boolean) || slide.signature) && (
                  <div
                    className={`${styles.slideCaption} ${
                      isCaptionVisible ? styles.slideCaptionActive : ''
                    } ${isCaptionInstant ? styles.slideCaptionInstant : ''}`}>
                    <div className={styles.captionTextGroup}>
                      {(slide.captionSegments[0] || slide.signature) && (
                        <div className={styles.captionTopRow}>
                          {slide.captionSegments[0] && (
                            <span className={styles.captionLine1}>{slide.captionSegments[0]}</span>
                          )}
                          {slide.signature && (
                            <a
                              href={
                                slide.signature.startsWith('http')
                                  ? slide.signature
                                  : `https://${slide.signature}`
                              }
                              onClick={(e) =>
                                handleDetailsClick(
                                  e,
                                  slide.signature.startsWith('http')
                                    ? slide.signature
                                    : `https://${slide.signature}`,
                                )
                              }
                              className={styles.detailsButton}
                              style={{
                                transitionDelay:
                                  captionAnimate && index === captionSlide && !isAutoTransitioning
                                    ? `${getDetailsButtonDelay(slide.captionSegments)}s`
                                    : '0s',
                              }}>
                              Подробнее
                              <span className={styles.detailsArrow} aria-hidden='true'>
                                <svg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                  <path
                                    d='M3.5 8h7.25M9.25 5.25 12.5 8l-3.25 2.75'
                                    stroke='currentColor'
                                    strokeWidth='1.75'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                  />
                                </svg>
                              </span>
                            </a>
                          )}
                        </div>
                      )}
                      {slide.captionSegments[1] && (
                        <span className={styles.captionLine2}>{slide.captionSegments[1]}</span>
                      )}
                      {slide.captionSegments[2] && (
                        <span className={styles.captionLine3}>{slide.captionSegments[2]}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
          })}
        </div>

        {slides.length > 1 && (
          <>
            <button type='button' className={styles.prevButton} onClick={prevSlide} aria-label='Предыдущий слайд'>
              ‹
            </button>
            <button type='button' className={styles.nextButton} onClick={nextSlide} aria-label='Следующий слайд'>
              ›
            </button>
            <div className={styles.dotsContainer}>
              {slides.map((_, index) => (
                <button
                  key={index}
                  type='button'
                  className={`${styles.dot} ${index === currentSlide ? styles.activeDot : ''}`}
                  onClick={() => goToSlide(index, { manual: true })}
                  aria-label={`Перейти к слайду ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MainSlider;
